import { v4 as uuid } from 'uuid'
import { OrderTypeEnum } from './OrderTypeEnum'
import { IOrderEntry, OrderEntry } from './OrderEntry/OrderEntry'
import { OrderStatusEnum } from './OrderStatusEnum'

export interface IOrder {
  id: string
  number: string
  type: OrderTypeEnum
  status: OrderStatusEnum
  datePlaced?: Date
  dateShipped?: Date
  dateSubmittedForApproval?: Date
  dateExpected?: Date
  dateCheckedIn?: Date
  orderedByUserId: string // ordered, pending approval
  approvedByUserId?: string // shipment approval for vendor, placement approval for restaurant
  receivedByUserId?: string // restaurant user who received the order
  checkedInByUserId?: string // restaurant user who checked in order
  orderedByCompanyId: string
  orderedByCompanyName: string
  assignedCompanyId: string // vendor or restaurant interested in order
  restaurantOrderId?: string // related restaurant order
  originalOrderId?: string // originating order for a backorder
  vendorRepId?: string
  invoiceNumber: string
  readReceipt?: { // who and when the order was viewed by
    userId: string,
    date: Date
  }
  orderEntries: IOrderEntry[]
}

export class Order implements IOrder {
  public id: string = uuid()
  public orderEntries: OrderEntry[] = []
  public status: OrderStatusEnum = OrderStatusEnum.CREATED
  datePlaced?: Date
  dateShipped?: Date
  dateExpected?: Date
  dateSubmittedForApproval?: Date
  dateCheckedIn?: Date
  approvedByUserId?: string
  receivedByUserId?: string
  checkedInByUserId?: string
  restaurantOrderId?: string
  originalOrderId?: string
  vendorRepId?: string
  public invoiceNumber: string = ''
  readReceipt?: {
    userId: string,
    date: Date
  }

  constructor(
      public number: string,
      public type: OrderTypeEnum,
      public orderedByUserId: string,
      public orderedByCompanyId: string,
      public orderedByCompanyName: string,
      public assignedCompanyId: string
  ) {}

  public approve(userId: string) {
    if (this.canApprove) {
      this.status = OrderStatusEnum.APPROVED
      this.approvedByUserId = userId
      this.datePlaced = new Date()
    }
  }

  public ship(userId: string) {
    if (this.canShip) {
      this.status = OrderStatusEnum.SHIPPED
      this.approvedByUserId = userId
      this.dateShipped = new Date()
    }
  }

  public reject(userId: string) {
    if (this.canApprove) {
      this.status = OrderStatusEnum.REJECTED
      this.dateSubmittedForApproval = undefined
    }
  }

  public receive(userId: string) {
    if (this.canReceive) {
      this.status = OrderStatusEnum.RECEIVED
      this.receivedByUserId = userId
    }
  }

  public checkIn(userId: string) {
    this.status = OrderStatusEnum.CHECKED_IN
    this.checkedInByUserId = userId
    this.dateCheckedIn = new Date()
  }

  public submitForApproval(userId: string) {
    if (this.canEdit) {
      this.status = OrderStatusEnum.PENDING
      this.orderedByUserId = userId
      this.dateSubmittedForApproval = new Date()
    }
  }

  get monthAndYearPlaced(): number {
    if (this.status === OrderStatusEnum.DRAFT) {
      return 1000001
    }

    return this.datePlaced ? (this.datePlaced.getFullYear() * 100) + (this.datePlaced.getMonth() + 1) : 1000000
  }

  get monthAndYearExpected(): number {
    return this.dateExpected ? (this.dateExpected.getFullYear() * 100) + (this.dateExpected.getMonth() + 1) : 1000000
  }

  get totalValue(): number {
    return this.orderEntries
        .reduce((v: number, c: OrderEntry) => v + ( c.pricePerUnit || 0 ) * c.quantity, 0)
  }

  get percentDelivered(): number {
    const complete = this.orderEntries.filter((entry: OrderEntry) => entry.isComplete)
    return complete.length * 100 / this.orderEntries.length
  }

  get canEdit(): boolean {
    return this.status === OrderStatusEnum.CREATED
      || this.status === OrderStatusEnum.REJECTED
      || this.status === OrderStatusEnum.DRAFT
  }

  get canApprove(): boolean {
    return this.status === OrderStatusEnum.PENDING
  }

  get canShip(): boolean {
    return this.status === OrderStatusEnum.CREATED
  }

  get canReceive(): boolean {
    return this.status === OrderStatusEnum.SHIPPED
  }

  get canDelete(): boolean {
    const deletableStatuses = [
      OrderStatusEnum.CREATED,
      OrderStatusEnum.PENDING,
      OrderStatusEnum.REJECTED,
      OrderStatusEnum.DRAFT
    ];

    return deletableStatuses.includes(this.status)
  }

  get isApproved(): boolean {
    return this.status === OrderStatusEnum.APPROVED
  }

  get isShipped(): boolean {
    return this.status === OrderStatusEnum.SHIPPED
  }

  get isReceived(): boolean {
    return this.status === OrderStatusEnum.RECEIVED || this.isComplete
  }

  get isComplete(): boolean {
    return (
        this.type === OrderTypeEnum.RESTAURANT
        && this.status === OrderStatusEnum.APPROVED
        && this.percentDelivered === 100
      )
      || ( this.type === OrderTypeEnum.VENDOR && this.status === OrderStatusEnum.CHECKED_IN )
      || this.status === OrderStatusEnum.REJECTED
  }
}

